import { selectionMap, qaMap, qaAnswerMap } from "../config/promotionOnboardingMaps";

/**
 * Helper to process and set Mixpanel User Properties for promotion onboarding
 */

interface SingleSelectionData {
  pageKey: string;
  singleSelectionId: number;
}

interface MultiSelectionData {
  pageKey: string;
  multiSelectionIdList: number[];
}

interface QAData {
  pageKey: string;
  qaList: Array<{ answerId: number }>;
}

/**
 * Process single selection and return properties to set
 */
export function processSingleSelection(
  data: SingleSelectionData
): Record<string, string> | null {
  const { pageKey, singleSelectionId } = data;

  if (!selectionMap[pageKey]) {
    return null;
  }

  const map = selectionMap[pageKey];
  const question = Object.keys(map)[0];
  const answers = map[question];

  if (!answers || singleSelectionId < 1 || singleSelectionId > answers.length) {
    return null;
  }

  const answer = answers[singleSelectionId - 1];
  const properties: Record<string, string> = {
    [question]: answer,
  };

  return properties;
}

/**
 * Process multi selection and return properties to set
 */
export function processMultiSelection(
  data: MultiSelectionData
): Record<string, string[]> | null {
  const { pageKey, multiSelectionIdList } = data;

  if (!selectionMap[pageKey] || !multiSelectionIdList || multiSelectionIdList.length === 0) {
    return null;
  }

  const map = selectionMap[pageKey];
  const question = Object.keys(map)[0];
  const answerOptions = map[question];

  if (!answerOptions) {
    return null;
  }

  const selectedAnswers: string[] = [];
  const sortedIds = [...multiSelectionIdList].sort((a, b) => a - b);

  sortedIds.forEach((id) => {
    if (id >= 1 && id <= answerOptions.length) {
      selectedAnswers.push(answerOptions[id - 1]);
    }
  });

  if (selectedAnswers.length === 0) {
    return null;
  }

  return {
    [question]: selectedAnswers,
  };
}

/**
 * Process QA responses and return properties to set
 */
export function processQAResponses(data: QAData): Record<string, string> | null {
  const { pageKey, qaList } = data;

  if (!qaMap[pageKey] || !qaAnswerMap[pageKey] || !qaList || qaList.length === 0) {
    return null;
  }

  const questions = qaMap[pageKey];
  const answerOptions = qaAnswerMap[pageKey];
  const properties: Record<string, string> = {};

  qaList.forEach((qaItem, index) => {
    if (index < questions.length) {
      const question = questions[index];
      const answerId = qaItem.answerId;

      if (answerId >= 1 && answerId <= answerOptions.length) {
        const answer = answerOptions[answerId - 1];
        properties[question] = answer;
      }
    }
  });

  return Object.keys(properties).length > 0 ? properties : null;
}
